import type { Task } from './api';

/**
 * Today's list, in the three piles its rows already say they are in.
 *
 * The shortlist was one flat column in `rankCandidate`'s order, and on the ADHD persona's account
 * that meant 48 rows — 11 started, 3 next and 34 captured in bursts and never touched — with a
 * Start on every one and nothing saying where to begin. The fold could not bound it, rightly: a
 * capture carries no estimate, so it claims no minutes and pushes no line (`Today.tsx` explains
 * why it must not be given a made-up one). The grouping is the cut the fold cannot make.
 *
 * **Nothing is chosen or re-ranked here.** Each group is a stable filter of the order the server
 * sent, so inside a group the rows stay in `rankCandidate`'s order — deadline first since
 * `1fe5765` — and this module cannot drift from it by having an opinion of its own. Deciding *for*
 * the reader what to do first is the half of `01a02f8e-b626` that was deliberately left open.
 *
 * Here rather than in `packages/core` because it judges nothing the row does not already carry:
 * a status, whether a date or an estimate or a priority was ever set, whether it was ever filed.
 * No zone and no clock — which is the line `timezones.ts` draws between what the browser may
 * compute and what only the server may. The Tasks list's sections (`01a08097-0822`) are meant to
 * use this same rule, which is why it is a module and not a block inside `Today.tsx`.
 */

/** In the order they are drawn. */
export type ShortlistGroupId = 'started' | 'dueOrPlanned' | 'captured';

/**
 * Captured and never sorted: still in the inbox, and nobody has decided one thing about it.
 *
 * No deadline, no plan, no estimate, no priority — the four the task and the decision name — and
 * also never filed under a goal, a project, a parent or a label. Each of those is a decision
 * somebody made about the row, which is exactly what "never sorted" says has not happened; a
 * capture tagged `work` has been looked at once, and folding it away with the untouched ones would
 * be the list forgetting that.
 */
export function isCapturedNeverSorted(task: Task): boolean {
  return (
    task.status === 'inbox' &&
    task.dueAt === null &&
    task.scheduledDate === null &&
    task.estimateMinutes === null &&
    task.priority === 0 &&
    task.goalIds.length === 0 &&
    task.projectId === null &&
    task.parentTaskId === null &&
    task.labelIds.length === 0
  );
}

/**
 * Which pile a row is in.
 *
 * Started wins over everything: a started task that is also overdue is still the thing to finish
 * rather than a thing to start, and its row carries its own late pill. The middle group is every
 * row that is neither started nor an untouched capture — due, planned, carried over, or decided on
 * (`next`, a priority, an estimate) — which is what makes it a shortlist and the third not one.
 */
function shortlistGroupOf(task: Task): ShortlistGroupId {
  if (task.status === 'in_progress') return 'started';
  return isCapturedNeverSorted(task) ? 'captured' : 'dueOrPlanned';
}

/** The ranked list split three ways, each group in the order it arrived. */
export function groupShortlist(ranked: readonly Task[]): Record<ShortlistGroupId, Task[]> {
  const groups: Record<ShortlistGroupId, Task[]> = { started: [], dueOrPlanned: [], captured: [] };
  for (const task of ranked) groups[shortlistGroupOf(task)].push(task);
  return groups;
}

/**
 * How many captures one press shows.
 *
 * Also the size below which there is nothing to fold: a group of five or fewer is already the
 * batch the row would offer, so it is drawn open rather than hidden behind a row promising itself.
 */
export const CAPTURED_BATCH = 5;

/** The in-progress rows the shortlist does not hold, by the reason it does not. */
export interface InProgressElsewhere {
  /** Somebody outside the app has the ball; the Waiting rail above draws these. */
  waiting: number;
  /** Behind another open task; offered nowhere on Today. */
  blocked: number;
  /** Promised to a later day, under a goal set aside, already planned in full, or past the cap. */
  other: number;
}

/**
 * Which of the Board's in-progress rows are not under Today's *Started* heading, and why not.
 *
 * The heading counts its own rows and the Board's sentence counts every `in_progress` row, and
 * they are different piles: `pickCandidates` drops a started task that is waiting or blocked, the
 * day plan withholds work under a parked goal, and a started row dated for a later day is not
 * today's. Printed bare, *Started 3* under *4 things in progress* is two figures over one word with
 * nothing on screen to reconcile them — the defect `01a15a31-14a5` closed for the late pile, and
 * the condition the architect named for reopening `01a15e3c-3b4c`. So the heading says the
 * difference instead: *Started 3 + 1 waiting*.
 *
 * Waiting is asked before blocked because that is the order `pickCandidates` asks, and because a
 * wait is the one of the two Today draws (in the Waiting rail). The Board folds a subtask under an
 * in-progress parent and the shortlist does not, so the heading can also be the *larger* figure;
 * that difference is rows the reader can see under the heading, and needs no words.
 */
export function inProgressElsewhere(
  inProgress: readonly Task[],
  started: readonly Task[],
): InProgressElsewhere {
  const held = new Set(started.map((t) => t.id));
  const out: InProgressElsewhere = { waiting: 0, blocked: 0, other: 0 };
  for (const task of inProgress) {
    if (held.has(task.id)) continue;
    if (task.isWaiting) out.waiting += 1;
    else if (task.isBlocked) out.blocked += 1;
    else out.other += 1;
  }
  return out;
}

/** The difference in words, or `null` when there is none: "1 waiting, 2 not on this list". */
export function elsewhereText({ waiting, blocked, other }: InProgressElsewhere): string | null {
  const parts = [
    waiting > 0 && `${waiting} waiting`,
    blocked > 0 && `${blocked} blocked`,
    other > 0 && `${other} not on this list`,
  ].filter((p): p is string => p !== false);
  return parts.length === 0 ? null : parts.join(', ');
}
